import { useState } from "react";
import {
  FORWARD_SKILLS,
  GOALKEEPER_SKILLS,
  SKILL_LABELS,
  type Position,
  type Profile,
  type Skill,
} from "../data/schema";
import { Button, Chip, Screen } from "./ui";

export function ProfilePicker({
  profiles,
  onPick,
  onEdit,
  onHistory,
  onReviewVideos,
}: {
  profiles: Profile[];
  onPick: (p: Profile) => void;
  onEdit: (p: Profile) => void;
  onHistory: (p: Profile) => void;
  onReviewVideos: () => void;
}) {
  return (
    <Screen title="Who's training?">
      <div className="space-y-4">
        {profiles.map((p) => (
          <div key={p.id} className="card overflow-hidden">
            <button onClick={() => onPick(p)} className="w-full p-5 text-left">
              <div className="flex items-center justify-between">
                <span className="h-display text-4xl">{p.name}</span>
                <span className="rounded-full bg-[var(--color-pitch)] px-3 py-1 text-base font-bold text-white">
                  {p.position === "goalkeeper" ? "Keeper" : "Forward"} · {p.age}
                </span>
              </div>
              <p className="mt-2 text-base text-neutral-600">
                {p.focusAreas.map((s) => SKILL_LABELS[s] ?? s).join(" · ")}
              </p>
              <p className="mt-3 h-display text-xl text-[var(--color-pitch-dark)]">
                Create today&apos;s plan →
              </p>
            </button>
            <div className="flex border-t border-[var(--color-line)]">
              <button onClick={() => onHistory(p)} className="flex-1 py-3 font-semibold text-neutral-600">
                History
              </button>
              <button onClick={() => onEdit(p)} className="flex-1 border-l border-[var(--color-line)] py-3 font-semibold text-neutral-600">
                Edit focus
              </button>
            </div>
          </div>
        ))}
      </div>

      <button onClick={onReviewVideos} className="mt-8 w-full text-neutral-500 underline">
        Parent: review drill videos
      </button>
    </Screen>
  );
}

export function ProfileEditor({
  profile,
  onSave,
  onCancel,
}: {
  profile: Profile;
  onSave: (p: Profile) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(profile.name);
  const [age, setAge] = useState(profile.age);
  const [position, setPosition] = useState<Position>(profile.position);
  const [focus, setFocus] = useState<Skill[]>(profile.focusAreas);

  const skillList = position === "goalkeeper" ? GOALKEEPER_SKILLS : FORWARD_SKILLS;
  const toggle = (s: Skill) =>
    setFocus((f) => (f.includes(s) ? f.filter((x) => x !== s) : [...f, s]));

  return (
    <Screen
      title={`Edit ${profile.name}`}
      right={
        <button onClick={onCancel} className="text-lg font-semibold text-[var(--color-pitch)]">
          Cancel
        </button>
      }
    >
      <label className="mb-4 block">
        <span className="mb-1 block font-semibold">Name</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="tap w-full rounded-xl border border-[var(--color-line)] px-4 text-xl"
        />
      </label>
      <label className="mb-4 block">
        <span className="mb-1 block font-semibold">Age</span>
        <input
          type="number"
          value={age}
          onChange={(e) => setAge(Number(e.target.value))}
          className="tap w-40 rounded-xl border border-[var(--color-line)] px-4 text-xl"
        />
      </label>

      <div className="mb-4">
        <span className="mb-2 block font-semibold">Position</span>
        <div className="flex gap-2">
          <Chip active={position === "forward"} onClick={() => setPosition("forward")}>
            Forward
          </Chip>
          <Chip active={position === "goalkeeper"} onClick={() => setPosition("goalkeeper")}>
            Goalkeeper
          </Chip>
        </div>
      </div>

      <div className="mb-6">
        <span className="mb-2 block font-semibold">What do you want to improve?</span>
        <div className="flex flex-wrap gap-2.5">
          {skillList.map((s) => (
            <Chip key={s} active={focus.includes(s)} onClick={() => toggle(s)}>
              {SKILL_LABELS[s] ?? s}
            </Chip>
          ))}
        </div>
      </div>

      <Button
        className="w-full"
        disabled={!name || focus.length === 0}
        onClick={() => onSave({ ...profile, name, age, position, focusAreas: focus })}
      >
        Save
      </Button>
    </Screen>
  );
}
